import { useForm } from 'react-hook-form';

import Button from '@helsenorge/designsystem-react/components/Button';
import FormFieldTag from '@helsenorge/designsystem-react/components/FormFieldTag';
import HighlightPanel from '@helsenorge/designsystem-react/components/HighlightPanel';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import PaperPlane from '@helsenorge/designsystem-react/components/Icons/PaperPlane';
import Input from '@helsenorge/designsystem-react/components/Input';
import Label from '@helsenorge/designsystem-react/components/Label';
import NotificationPanel from '@helsenorge/designsystem-react/components/NotificationPanel';
import Select from '@helsenorge/designsystem-react/components/Select';
import Textarea from '@helsenorge/designsystem-react/components/Textarea';
import Title from '@helsenorge/designsystem-react/components/Title';
import Validation from '@helsenorge/designsystem-react/components/Validation';

import { sanitizeValue } from '@helsenorge/core-cms/userfeedback/utils/string';

import { sendEmail } from '../../_api/ContactApi';
import { removeSsn, isEmail } from '../../_helpers/string';
import SafeHTML from '../SafeHtml';

import styles from './styles.module.scss';

interface Category {
  title: string;
  description: string;
}
interface ContactForm {
  title?: string;
  introduction?: string;
  categoryLabel: string;
  categoryValidationMessage: string;
  chooseCategoryLabel: string;
  categories: Category[];
  nameLabel: string;
  emailLabel: string;
  emailValidationMessage: string;
  feedbackLabel: string;
  feedbackValidationMessage: string;
  feedbackHelpText?: string;
  submitText: string;
  formValidationMessage: string;
  successMessage: string;
  sendEmailUrl: string;
}

interface ContactFormProps {
  contactForm: ContactForm;
  feedbackFieldMaxCharacters: number;
  resources?: {
    submitError?: string;
    characters: string;
    requiredField: string;
    optionalField: string;
  };
}

interface ContactFormData {
  date: string;
  category: string;
  name: string;
  email: string;
  feedback: string;
}

const ContactForm: React.FunctionComponent<ContactFormProps> = ({ contactForm, feedbackFieldMaxCharacters, resources }) => {
  const {
    register,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    handleSubmit,
    setError,
    watch,
  } = useForm<ContactFormData>({
    defaultValues: {
      date: '',
      category: '',
      name: '',
      email: '',
      feedback: '',
    },
  });

  const {
    title,
    introduction,
    categoryLabel,
    chooseCategoryLabel,
    categories,
    nameLabel,
    emailLabel,
    feedbackLabel,
    feedbackHelpText,
    submitText,
    categoryValidationMessage,
    emailValidationMessage,
    feedbackValidationMessage,
    formValidationMessage,
    successMessage,
    sendEmailUrl,
  } = contactForm;

  const maxFeedbackLength = feedbackFieldMaxCharacters || 500;

  const category = watch('category');
  const { description } = categories.find(c => c.title === category) || {};

  const categoryFormFieldTagId = 'category-form-field-tag';
  const nameFormFieldTagId = 'name-form-field-tag';
  const emailFormFieldTagId = 'email-form-field-tag';
  const feedbackFormFieldTagId = 'feedback-form-field-tag';
  const feedbackHelpTextId = 'feedback-help-text';
  const feedbackErrorTextId = 'feedback-error-text';

  const onSubmit = async (data: ContactFormData): Promise<void> => {
    try {
      await sendEmail({ sendEmailUrl, ...data });
    } catch {
      setError('root', {
        type: 'fetch',
        message: resources?.submitError,
      });
    }
  };

  if (isSubmitSuccessful) {
    return (
      <HighlightPanel color="blueberry" svgIcon={PaperPlane}>
        {successMessage}
      </HighlightPanel>
    );
  }

  if (errors.root?.message) {
    return (
      <NotificationPanel variant="error" role="alert">
        {errors.root?.message}
      </NotificationPanel>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} method="post">
      <input
        type="text"
        name="date"
        id="date"
        title="date"
        className={styles.contactform__date}
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
      />

      {title && (
        <Title htmlMarkup="h2" appearance="title2">
          {title}
        </Title>
      )}

      {introduction && <p>{introduction}</p>}

      <Validation errors={errors} errorTitle={formValidationMessage}>
        <Select
          selectId="category"
          label={
            <Label
              formFieldTag={
                <FormFieldTag id={categoryFormFieldTagId} level="required-field" resources={{ requiredField: resources?.requiredField }} />
              }
              labelTexts={[{ text: categoryLabel }]}
            />
          }
          aria-describedby={categoryFormFieldTagId}
          disabled={isSubmitting}
          errorText={errors.category?.message}
          {...register('category', { required: categoryValidationMessage })}
        >
          <option value={''}>{chooseCategoryLabel}</option>
          {categories.map(({ title }) => (
            <option value={title} key={title}>
              {title}
            </option>
          ))}
        </Select>

        {description && <SafeHTML className="rich-text" aria-live="polite" html={description} />}

        {!description && (
          <Input
            inputId="name"
            label={
              <Label
                formFieldTag={<FormFieldTag id={nameFormFieldTagId} level="optional" resources={{ optional: resources?.optionalField }} />}
                labelTexts={[{ text: nameLabel }]}
              />
            }
            aria-describedby={nameFormFieldTagId}
            disabled={isSubmitting}
            autoComplete="name"
            {...register('name', { setValueAs: v => removeSsn(sanitizeValue(v)) })}
          />
        )}

        {!description && (
          <Input
            inputId="email"
            label={
              <Label
                formFieldTag={
                  <FormFieldTag id={emailFormFieldTagId} level="required-field" resources={{ requiredField: resources?.requiredField }} />
                }
                labelTexts={[{ text: emailLabel }]}
              />
            }
            aria-describedby={emailFormFieldTagId}
            type="email"
            disabled={isSubmitting}
            autoComplete="email"
            errorText={errors.email?.message}
            {...register('email', {
              required: emailValidationMessage,
              validate: v => isEmail(v) || emailValidationMessage,
              setValueAs: v => removeSsn(sanitizeValue(v)),
            })}
          />
        )}

        {!description && feedbackHelpText && (
          <NotificationPanel labelId={feedbackHelpTextId} className={styles.contactform__help}>
            {feedbackHelpText}
          </NotificationPanel>
        )}
        {!description && (
          <Textarea
            textareaId="feedback"
            label={
              <Label
                formFieldTag={
                  <FormFieldTag
                    id={feedbackFormFieldTagId}
                    level="required-field"
                    resources={{ requiredField: resources?.requiredField }}
                  />
                }
                labelTexts={[{ text: feedbackLabel }]}
              />
            }
            aria-describedby={[
              feedbackFormFieldTagId,
              errors.feedback?.message ? feedbackErrorTextId : undefined,
              feedbackHelpText ? feedbackHelpTextId : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={isSubmitting}
            maxCharacters={maxFeedbackLength}
            resources={{
              characters: resources?.characters,
            }}
            grow
            errorText={errors.feedback?.message}
            errorTextId={feedbackErrorTextId}
            {...register('feedback', {
              required: feedbackValidationMessage,
              maxLength: { value: maxFeedbackLength, message: feedbackValidationMessage },
              setValueAs: v => removeSsn(sanitizeValue(v)),
            })}
          />
        )}
      </Validation>

      {!description && (
        <Button type="submit" size="large" disabled={isSubmitting}>
          <Icon svgIcon={PaperPlane} />
          {submitText}
        </Button>
      )}
    </form>
  );
};

export default ContactForm;
